import { useCallback } from "react";
import { compareVersions } from "../logic/logics_common";
import { sendMessage } from "utils/webview";
import { v4 as uuidv4 } from "uuid";

type Params = {
  user: any;
  isInappWebview?: boolean;
  appVersion: string;
  setMixPanel: () => void;
};

/**
 * 결제 버튼 클릭 시 공통 트래킹 훅
 * 순서: Meta CAPI(>=1.1.99) → FB Fallback(>=1.1.17 & isFirst) → Firebase/Appsflyer(>=1.1.47 & isFirst) → Mixpanel
 * - 인앱 환경에서만 Meta/FB/Firebase/Appsflyer 전송 (웹은 Mixpanel만)
 * - 기존 컴포넌트들의 이벤트명/파라미터/가드를 그대로 유지
 */
export default function usePaymentButtonClickTracking({
  user,
  isInappWebview,
  appVersion,
  setMixPanel,
}: Params) {
  const trackPaymentClick = useCallback(() => {
    // In-app: Meta CAPI 우선, 구버전은 FB로 폴백, 그리고 Firebase/Appsflyer (최초 주문 조건)
    if (isInappWebview) {
      // Meta CAPI (>= 1.1.99)
      if (
        user?.isInsider !== true &&
        compareVersions(appVersion, "1.1.99") >= 0
      ) {
        if (user?.segmentType) {
          sendMessage(
            JSON.stringify({
              event: "send_meta_capi_event",
              params: {
                eventId: uuidv4(),
                eventName: `btn_click_payment_type_${user?.segmentType}`,
                eventParams: {
                  value: user?.segmentType === "a" ? 107000 : 0,
                  currency: "KRW",
                },
              },
            })
          );
        }
        sendMessage(
          JSON.stringify({
            event: "send_meta_capi_event",
            params: {
              eventId: uuidv4(),
              eventName: "btn_click_payment",
              eventParams: {
                value: 59000,
                currency: "KRW",
              },
            },
          })
        );
      }
      // FB fallback (>= 1.1.17 & isFirst)
      else if (
        user?.isInsider !== true &&
        compareVersions(appVersion, "1.1.17") >= 0
      ) {
        if (user?.segmentType) {
          sendMessage(
            JSON.stringify({
              event: "send_fb_event",
              params: {
                eventName: `btn_click_payment_type_${user?.segmentType}`,
              },
            })
          );
        }
        sendMessage(
          JSON.stringify({
            event: "send_fb_event",
            params: {
              eventName: "btn_click_payment",
            },
          })
        );
      }

      // Firebase/Appsflyer (>= 1.1.47 & isFirst)
      if (
        user?.isInsider !== true &&
        compareVersions(appVersion, "1.1.47") >= 0
      ) {
        if (user?.segmentType === "a") {
          sendMessage(
            JSON.stringify({
              event: "send_appsflyer_event",
              params: {
                eventName: "af_initiated_checkout_type_a",
              },
            })
          );

          sendMessage(
            JSON.stringify({
              event: "send_firebase_analytics_event",
              params: {
                eventName: "begin_checkout",
                eventParams: {
                  items: [
                    {
                      item_id: "distancing_program",
                      item_name: "distancing_program",
                    },
                  ],
                },
              },
            })
          );
        }

        sendMessage(
          JSON.stringify({
            event: "send_appsflyer_event",
            params: {
              eventName: "af_initiated_checkout",
            },
          })
        );
      }
    }

    setMixPanel();
  }, [
    user?.isInsider,
    user?.segmentType,
    isInappWebview,
    appVersion,
    setMixPanel,
  ]);

  return { trackPaymentClick } as const;
}
